import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Dropdown from '../dropdown';
import Button from '../button';
import InputField from '../formItem/inputField';
import { DIDIM_HW_FILTER_OPTIONS, API_PAGE_SIZE } from '../../constants';
import { didimAPI } from '../../actions/imsServer';

const DidimHwDialog = ({ open, onClose, onSelect, excludeIds = [] }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [didimHwFilter, setDidimHwFilter] = useState('unregistered');
    const [didimHwSearchText, setDidimHwSearchText] = useState('');
    const [didimHwList, setDidimHwList] = useState([]);
    const [isLoadingDidimHw, setIsLoadingDidimHw] = useState(false);

    // 팝업이 열릴 때마다 목록 가져오기
    useEffect(() => {
        if (open) {
            setDidimHwSearchText('');
            fetchDidimHwList();
        }
    }, [open]);

    // 디딤H/W 목록 가져오기 (100개씩 페이지네이션)
    const fetchDidimHwList = async () => {
        try {
            setIsLoadingDidimHw(true);
            const allDidims = [];
            const limit = API_PAGE_SIZE;
            let offset = 0;
            let hasMore = true;

            while (hasMore) {
                const response = await didimAPI.getDidims({
                    state: '0',
                    expand: 'organization',
                    limit,
                    offset,
                });
                const didims = response?.data?.didims ?? response?.didims ?? [];
                const list = Array.isArray(didims) ? didims : [];
                allDidims.push(...list);
                if (list.length < limit) {
                    hasMore = false;
                } else {
                    offset += limit;
                }
            }

            // API 응답 데이터를 필요한 형태로 변환
            const transformedData = allDidims.map(item => ({
                id: item.id,
                organizationName: item?.organization?.name || '-',
                name: item.name || `${t('didim')}${item.id}`,
                info: item.info || '',
                uid: item.uid || '',
                modelName: item.modelName || t('didim'),
                organizationId: item?.organizationId || 0,
            }));

            setDidimHwList(transformedData);
        } catch (error) {
            console.error('디딤H/W 목록 조회 실패:', error);
            setDidimHwList([]);
        } finally {
            setIsLoadingDidimHw(false);
        }
    };

    // 필터링된 디딤H/W 목록 반환
    const getFilteredList = () => {
        let filtered = didimHwList;

        // excludeIds에 포함된 항목 제외
        if (excludeIds.length > 0) {
            filtered = filtered.filter(item => !excludeIds.includes(item.id));
        }

        // 필터 적용
        if (didimHwFilter === 'unregistered') {
            // 미등록 디딤H/W만
            filtered = filtered.filter(item => item.organizationId === 0);
        } else if (didimHwFilter === 'search') {
            // 검색 옵션일 때는 검색어로만 필터링
            if (didimHwSearchText && didimHwSearchText.trim() !== '') {
                const searchLower = didimHwSearchText.toLowerCase().trim();
                filtered = filtered.filter(item =>
                    item.name && item.name.toLowerCase().includes(searchLower)
                );
            } else {
                // 검색어가 없으면 빈 목록
                filtered = [];
            }
        }
        // 'all' 옵션일 때는 필터링 없음

        return filtered;
    };

    // 드롭다운 변경 핸들러
    const handleDidimHwFilterChange = (value) => {
        const filterValue = typeof value === 'object' ? value.value : value;
        setDidimHwFilter(filterValue);
    };

    // 선택 핸들러
    const handleSelect = (didimHw) => {
        if (onSelect) {
            onSelect(didimHw);
        }
        onClose();
    };

    // 테이블 헤더 스타일
    const tableHeaderStyle = {
        p: 2,
        fontWeight: 600,
        fontSize: '14px'
    };

    // 테이블 데이터 셀 스타일
    const tableCellStyle = {
        p: 2,
        fontSize: '14px',
        color: theme.palette.figma.white[80]
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            slotProps={{
                root: { keepMounted: false },
            }}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    backgroundColor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.figma.white[20]}`,
                }
            }}
        >
            <DialogTitle sx={{
                textAlign: 'center',
                pb: 1,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Dropdown
                        value={didimHwFilter}
                        onChange={handleDidimHwFilterChange}
                        options={DIDIM_HW_FILTER_OPTIONS}
                        sx={{ minWidth: '200px' }}
                    />
                    {didimHwFilter === 'search' && (
                        <InputField
                            placeholder={t('search_by_didim_name')}
                            value={didimHwSearchText}
                            onChange={(e) => setDidimHwSearchText(e.target.value)}
                            size="small"
                            sx={{
                                flex: 1,
                                maxWidth: '300px',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: theme.palette.figma.white[10],
                                    borderRadius: '15px',
                                    height: '40px',
                                },
                            }}
                        />
                    )}
                </Box>
            </DialogTitle>

            <DialogContent sx={{
                p: 3,
                maxHeight: '70vh',
                overflowY: 'auto'
            }}>
                {isLoadingDidimHw ? (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px'
                    }}>
                        <Typography sx={{ color: theme.palette.figma.white[70] }}>
                            {t('loading_didim_h_w_list')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{
                        border: `1px solid ${theme.palette.figma.white[20]}`,
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '80px 1fr 1fr 1fr 1fr 1fr 100px',
                            backgroundColor: theme.palette.figma.white[10],
                            borderBottom: `1px solid ${theme.palette.figma.white[20]}`
                        }}>
                            <Typography sx={tableHeaderStyle}>
                                {t('didim_id')}
                            </Typography>
                            <Typography sx={tableHeaderStyle}>
                                {t('organization_name')}
                            </Typography>
                            <Typography sx={tableHeaderStyle}>
                                {t('didim_name')}
                            </Typography>
                            <Typography sx={tableHeaderStyle}>
                                {t('note')}
                            </Typography>
                            <Typography sx={tableHeaderStyle}>
                                {t('uid')}
                            </Typography>
                            <Typography sx={tableHeaderStyle}>
                                {t('model_name')}
                            </Typography>
                            <Typography sx={{ ...tableHeaderStyle, textAlign: 'center' }}>
                                {t('select')}
                            </Typography>
                        </Box>

                        {(() => {
                            const currentList = getFilteredList();
                            if (currentList.length === 0) {
                                return (
                                    <Box sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        color: theme.palette.figma.white[60]
                                    }}>
                                        <Typography>
                                            {didimHwFilter === 'unregistered' ? t('no_unregistered_didim_h_w') : t('no_didim_h_w')}
                                        </Typography>
                                    </Box>
                                );
                            }

                            return currentList.map((item, index) => (
                                <Box key={`${item.id}-${index}`} sx={{
                                    display: 'grid',
                                    alignItems: 'center',
                                    gridTemplateColumns: '80px 1fr 1fr 1fr 1fr 1fr 100px',
                                    borderBottom: index < currentList.length - 1 ? `1px solid ${theme.palette.figma.white[20]}` : 'none',
                                    '&:hover': {
                                        backgroundColor: theme.palette.figma.white[5]
                                    }
                                }}>
                                    <Typography sx={tableCellStyle}>
                                        {item.id}
                                    </Typography>
                                    <Typography sx={tableCellStyle}>
                                        {item.organizationName}
                                    </Typography>
                                    <Typography sx={tableCellStyle}>
                                        {item.name}
                                    </Typography>
                                    <Typography sx={tableCellStyle}>
                                        {item.info}
                                    </Typography>
                                    <Typography sx={tableCellStyle}>
                                        {item.uid}
                                    </Typography>
                                    <Typography sx={tableCellStyle}>
                                        {item.modelName}
                                    </Typography>
                                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {item.organizationId > 0 ? (
                                            <Typography sx={{
                                                fontSize: '12px',
                                                color: theme.palette.figma.white[60],
                                                fontStyle: 'italic'
                                            }}>
                                                {t('registered')}
                                            </Typography>
                                        ) : (
                                            <Button
                                                size="small"
                                                onClick={() => handleSelect(item)}
                                                sx={{ minWidth: '60px' }}
                                            >
                                                {t('select')}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            ));
                        })()}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                p: 3,
                gap: 2,
                justifyContent: 'center'
            }}>
                <Button
                    variant="gray"
                    onClick={onClose}
                    sx={{ minWidth: '120px' }}
                >
                    {t('cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DidimHwDialog;

